import type { UnitInfoMobileHSIInterface } from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import type { UnitConfigParams } from 'unitData/BaseUnitConfig';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import type { MobileHSIUnitInfoDataInterface } from './unitInfoData';

const RETURN_FLOW_CONFIGURATION_KEYS: Partial<
  Record<string, readonly number[]>
> = {
  QI341: [6, 7, 8],
  QI353: [9, 10, 11, 12, 13, 14],
  QI442: [9, 10, 11, 12, 13, 14],
};

export const canShowReturnFlow = (
  unitInfo: Pick<UnitInfoMobileHSIInterface, 'modelName' | 'configuration'>,
): boolean =>
  RETURN_FLOW_CONFIGURATION_KEYS[unitInfo.modelName]?.includes(
    unitInfo.configuration.configKey,
  ) ?? false;

export class MobileHSIConfig extends BaseUnitConfig {
  static getDefaultData(params?: UnitConfigParams): UnitInfoMobileHSIInterface {
    const unitInfoData =
      (params?.data as MobileHSIUnitInfoDataInterface | undefined) ?? {};
    const type = params?.unitType ?? UnitTypes.MOBILE_TRACKED_HSI;
    if (type === UnitTypes.MOBILE_TRACKED_HSI) {
      const defaultModel = Object.entries(unitInfoData)[3];
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      const hsiData = Object.values(modelData.configurations.HSI1.models)[0];

      return {
        ...BaseUnitConfig.getBaseDefaults(),
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 1,
        moisture: 0,
        newMoisture: false,
        HSI1: {
          modelName: hsiData.simulationModel,
          setting: hsiData.defaultSetting,
          motorFrequency: hsiData.defaultMotorFrequency,
          motorPower: hsiData.defaultMotorPower,
          tipSpeed: 29.1,
        },
        SCREEN1: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[1]
                  .defaultSeparationSize,
            },
          },
        },
        SCREEN2: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN2.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model]?.configurations?.SCREEN2?.decks[1]
                  ?.defaultSeparationSize || 25,
            },
          },
        },
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayConfig: false,
          displayLoad: true,
          displayFeederModel: false,
          displayFeederSeparation: false,
          displayCrusherModel: false,
          displayCrusherSetting: false,
          displayCrusherFrequency: false,
          displayCrusherMotorPower: false,
          displayCrusherTipSpeed: false,
          displayPanFeederModel: false,
          displayScreenSeparation: false,
          displayReturnFlow: false,
        },
        parallelUnits: 1,
      };
    } else {
      const defaultModel = Object.entries(unitInfoData)[0];
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      const hsiData = Object.values(modelData.configurations.HSI1.models)[0];

      return {
        ...BaseUnitConfig.getBaseDefaults(),
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 2,
        moisture: 0,
        newMoisture: false,
        HSI1: {
          modelName: hsiData.simulationModel,
          setting: hsiData.defaultSetting,
          motorFrequency: hsiData.defaultMotorFrequency,
          motorPower: hsiData.defaultMotorPower,
          tipSpeed: 33.4,
        },
        SCREEN1: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[1]
                  .defaultSeparationSize,
            },
          },
        },
        SCREEN2: {
          decks: {
            0: {
              separationSize: 0,
            },
            1: {
              separationSize: 0,
            },
          },
        },
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayConfig: false,
          displayLoad: true,
          displayFeederModel: false,
          displayFeederSeparation: false,
          displayCrusherModel: false,
          displayCrusherSetting: false,
          displayCrusherFrequency: false,
          displayCrusherMotorPower: false,
          displayCrusherTipSpeed: false,
          displayPanFeederModel: false,
          displayScreenSeparation: false,
          displayReturnFlow: false,
        },
        parallelUnits: 1,
      };
    }
  }

  static getNumberOfPorts(model?: string, config?: number): number {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return getNumberOfPorts(model!, config!);
  }
}

export const mobileHSIConfig = new MobileHSIConfig();

export const getNumberOfPorts = (model: string, configKey: number) => {
  const isKey = (...keys: number[]) => keys.includes(configKey);
  switch (model) {
    case 'QI341':
      if (isKey(0, 6)) {
        return 1;
      } else if (isKey(1, 2, 3, 7, 8)) {
        return 2;
      }

      return 3;
    case 'UI310+':
      if (isKey(0, 2)) {
        return 2;
      }
      return 1;
    default:
      if (isKey(0, 9)) {
        return 1;
      } else if (isKey(1, 2, 3, 10, 11, 13)) {
        return 2;
      } else if (isKey(4, 5, 7, 12, 14)) {
        return 3;
      }
      return 4;
  }
};
